/*
  Meta Pixel events — one door, one set of guards.

  The pixel is loaded and initialised by metaPixelLoader.js, and only after the
  visitor has explicitly accepted marketing/advertising tracking. This module
  is the only place that calls `fbq`: PageView (for MetaPixel.jsx, which owns
  the page count) and ViewContent for the launch page. Nothing here reads a
  token.

  ── Consent, first ─────────────────────────────────────────────────────────
  Every function below goes through the local `fbq` wrapper, which refuses to
  do anything unless isMetaTrackingAllowed() is true. Before consent there is
  no fbevents.js on the page, no fbq to call, no queue holding events back and
  nothing waiting to be flushed: a call made too early returns false and is
  forgotten. Nothing in this file stores an event for later.

  ── No replay ──────────────────────────────────────────────────────────────
  Consent does not resurrect what happened before it. ViewContent describes
  the page the visitor is still looking at, so MobileAppLanding re-attempts it
  on the grant (see onMarketingConsent in consent.js). PageView likewise
  applies to the page currently on screen.

  Three problems this file exists to solve:

    1. StrictMode. index.js renders under <React.StrictMode>, so in development
       every effect runs twice and every component mounts twice. A conversion
       event fired from an effect or a handler must therefore be idempotent by
       construction, not by hoping the effect runs once.

    2. Remounts. MobileAppLanding hides itself behind `display: none` rather
       than unmounting when the enquiry form opens, but nothing guarantees that
       forever. Every guard below lives at module scope, which outlives any component instance and
       is unaffected by StrictMode's double invocation.

    3. Consent. The gate has to be impossible to forget at a call site, so it
       lives in the one function all of them already go through rather than in
       each of them.


  There is deliberately no personal data in any payload. Advanced matching
  (hashed email) is a separate decision with its own consent question; see the
  privacy note in the implementation report.
*/
import { ensureMetaPixel, isMetaTrackingAllowed } from "./metaPixelLoader";

/* Events that must happen at most once per page load, by key. Module scope, so
   a StrictMode double-effect or a component remount both find the key already
   present. */
const firedOnce = new Set();

/*
  The one door. Two things happen here and nowhere else:

    - consent is checked. No consent, or a revoke, and the call is dropped on
      the spot: nothing is loaded, nothing is sent, nothing is remembered.

    - the pixel is loaded, lazily, on the first event that is allowed to go
      out. So consent is what puts fbevents.js on the page, not a page load.
      ensureMetaPixel asks consent.js itself, so it cannot be talked into
      loading by a caller that only thinks it has consent.

  ensureMetaPixel installs window.fbq synchronously and the stub queues calls
  until the library arrives, so a missing fbq at this point means the script
  was blocked (an ad blocker, tracking protection, jsdom in the test suite) —
  never "too early". Nothing to do but skip.
*/
function fbq(...args) {
  if (!isMetaTrackingAllowed()) return false;
  ensureMetaPixel();
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq(...args);
    return true;
  }
  return false;
}

/**
 * PageView — one page of the site was looked at.
 *
 * No once-only guard here: a PageView per page is the point. Which pages have
 * already been counted is MetaPixel.jsx's business, because only it knows what
 * a "page" is in a client-side router. This function only asks whether it is
 * allowed to send at all.
 */
export function trackPageView() {
  return fbq("track", "PageView");
}

/**
 * ViewContent — the visitor reached the launch page.
 *
 * Once per page load. A client-side navigation away to /platform and back does
 * not re-fire it; the PageView for that navigation is MetaPixel.jsx's job.
 */
export function trackViewContent() {
  /* Asked before the once-only guard, not after: a call made without consent
     must leave no trace, or it would burn the key and silence the event for
     the rest of the page after the visitor accepts. */
  if (!isMetaTrackingAllowed()) return false;
  if (firedOnce.has("ViewContent")) return false;
  firedOnce.add("ViewContent");
  return fbq("track", "ViewContent", {
    content_name: "Sentio Mind App",
    content_category: "launch",
  });
}

/** Test-only. The guards are module state on purpose, so tests must clear it. */
export function resetTrackingForTests() {
  firedOnce.clear();
}
